/* eslint-disable no-restricted-syntax */
import gql from 'graphql-tag'
import { log } from '@/services/logging-service'
import { getApolloClient } from '@/lib/apollo-client'

/**
 * GraphQL query to retrieve current weather conditions for a given coordinate
 *
 * @param {Geographic} coord - a Geographic object containing lat/lon coords for the requested location
 * @returns {Object} - the current weather conditions for the requested location
 */
export const getCurrentWindConditionsQuery = async (coord) => {
  const operation = 'weatherConditionsCurrentWindQuery'
  const query = gql`
    query ${operation}(
      $interval: WeatherInterval!
      $location: WeatherLocationInput!
    ) {
      weatherConditions(interval: $interval, location: $location) {
        periods {
          windDirection
          windVelocity
        }
      }
    }
  `

  const location = {
    coordinate: formatLocationForQuery(coord),
  }

  const client = await getApolloClient()
  const { errors, data } = await client.query({
    query,
    variables: {
      interval: 'MINUTE',
      location,
    },
  })

  if (errors || !data) {
    log('Error retrieving weather data', errors)
  }

  return data
}

/**
 * GraphQL query to get wind conditions in bulk for multiple coordinates
 * @param {Array[Object]} points - array of points to get wind conditions for
 * @returns {Object} wind conditions for each of the provided points
 */
export const getBulkWindConditionsQuery = async (points) => {
  const fields = []
  const args = ['$interval: WeatherInterval!']
  const variables = {
    interval: 'MINUTE',
  }

  points.forEach((point, index) => {
    args.push(`$Location${index}: WeatherLocationInput!`)

    fields.push(`
      WindCondition${index}: weatherConditions(location: $Location${index}, interval: $interval) {
          periods {
            windDirection
            windVelocity
          }
      }
    `)

    variables[`Location${index}`] = {
      coordinate: formatLocationForQuery(point.coordinate),
    }
  })

  const query = gql`
    query weatherConditionsBulkWindQuery(
      ${args.join('\n')}
    ) {
      ${fields.join('\n')}
    }
  `

  const client = await getApolloClient()
  const { errors, data } = await client.query({
    query,
    variables,
  })

  if (errors || !data) {
    log('Error retrieving weather data', errors)
  }

  return data
}

/**
 * GraphQL query to retrieve weather data for a given coordinate
 *
 * @param {Geographic} coord - a Geographic object containing lat/lon coords for the requested location
 * @returns {Object} - the weather conditions for the requested location
 */
export const getAllWeatherDataQuery = async (coord) => {
  const query = gql`
    query FetchWeatherSummary(
      $location: WeatherLocationInput!
      $now: Time!
      $oneDayAgo: Time!
      $sevenDaysLater: Time!
      $firstDayOfMonth: Time!
      $lastDayOfMonth: Time!
    ) {
      dailyForecast: weatherForecastDaily(
        to: $sevenDaysLater
        from: $now
        location: $location
      ) {
        timezone
        days {
          startTime
          endTime
          maxFeelsLike
          minFeelsLike
          maxTemperature
          minTemperature
          precipitation
          snowFallRate
          percentChanceOfPrecipitation
          description
          iconName
          windDirection
          windVelocity
          solarLunar {
            solar {
              rise
              set
            }
            lunar {
              rise
              set
            }
          }
          hours {
            startTime
          }
        }
      }
      hourlyForecast: weatherForecastHourly(
        to: $sevenDaysLater
        from: $now
        location: $location
      ) {
        timezone
        hours {
          startTime
          endTime
          feelsLike
          temperature
          windDirection
          windVelocity
          pressure
          precipitation
          snowFallRate
          description
          iconName
          percentChanceOfPrecipitation
        }
      }
      historicalConditions: weatherConditions(
        interval: HOUR
        location: $location
        from: $oneDayAgo
        to: $now
      ) {
        timezone
        periods {
          startTime
          endTime
          feelsLike
          temperature
          windDirection
          windVelocity
          pressure
          precipitation
          snowFallRate
          description
          iconName
        }
      }
      currentConditions: weatherConditions(
        interval: MINUTE
        location: $location
      ) {
        timezone
        periods {
          startTime
          endTime
          feelsLike
          temperature
          windDirection
          windVelocity
          pressure
          precipitation
          snowFallRate
          description
          iconName
          percentChanceOfPrecipitation
        }
      }
      solarLunar: solarLunarData(
        from: $firstDayOfMonth
        to: $lastDayOfMonth
        location: $location
      ) {
        timezone
        periods {
          startTime
          endTime
          lunar {
            illumination
            overhead
            phaseName
            rise
            set
            underfoot
          }
          solar {
            rise
            set
            transit
          }
        }
      }
    }
  `

  const location = {
    coordinate: formatLocationForQuery(coord),
  }

  // now will be set to start of the minute so apollo caching works
  let now = new Date()
  now.setSeconds(0, 0)
  now = new Date(now).toISOString()

  let oneDayAgo = new Date(now)
  oneDayAgo.setDate(oneDayAgo.getDate() - 1)
  oneDayAgo = new Date(oneDayAgo).toISOString()

  let sevenDaysLater = new Date(now)
  sevenDaysLater.setDate(sevenDaysLater.getDate() + 7)
  sevenDaysLater = new Date(sevenDaysLater).toISOString()

  // set hours to 12pm so timezone conversion remains in same day
  const firstDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth(),
    1,
    12
  ).toISOString()

  const lastDayOfMonth = new Date(
    new Date().getFullYear(),
    new Date().getMonth() + 1,
    0,
    12
  ).toISOString()

  const client = await getApolloClient()

  const response = await client.query({
    query,
    variables: {
      location,
      now,
      oneDayAgo,
      firstDayOfMonth,
      lastDayOfMonth,
      sevenDaysLater,
    },
  })

  return response
}

/**
 * GraphQL query to retrieve the data used to populate the wind calendar (forecasts and historical)
 *
 * @param {Array<Object>} points - array of points containing lat/lon coords for the requested location
 * @returns {Object} - the weather data for the wind calendar for the requested location
 */
export const getWindCalendarWeatherQuery = async (points) => {
  // now will be set to start of hour so apollo caching works
  let currentHour = new Date()
  currentHour.setMinutes(0, 0, 0)
  currentHour = new Date(currentHour).toISOString()

  // from midnight of current day
  let from = new Date()
  from.setHours(0, 0, 0, 0)
  from = new Date(from).toISOString()

  // to 7 days later
  let to = new Date()
  to.setDate(to.getDate() + 7)
  to.setMinutes(0, 0, 0)
  to = new Date(to).toISOString()

  const fields = []
  const args = ['$now: Time!', '$to: Time!', '$from: Time!']
  const variables = {
    to,
    now: currentHour,
    from,
  }

  points.forEach((point, index) => {
    args.push(`$location${index}: WeatherLocationInput!`)

    fields.push(`
       hourlyForecast${index}: weatherForecastHourly(
        from: $now
        location: $location${index}
        to: $to
      ) {
        timezone
        hours {
          startTime
          windDirection
          windVelocity
        }
      }
      historicalConditions${index}: weatherConditions(
        interval: HOUR
        location: $location${index}
        from: $from
        to: $now
      ) {
        timezone
        periods {
          startTime
          windDirection
          windVelocity
        }
      }
      solarLunar${index}: solarLunarData(from: $from, to: $to, location: $location${index}) {
        timezone
        periods {
          startTime
          solar {
            rise
            set
          }
        }
      }
    `)

    variables[`location${index}`] = {
      coordinate: formatLocationForQuery(point.coordinate),
    }
  })

  const query = gql`
    query weatherWindCalendarQuery(
      ${args.join('\n')}
    ) {
      ${fields.join('\n')}
    }
  `

  const client = await getApolloClient()

  const response = await client.query({
    query,
    variables,
  })

  return response
}

/**
 * GraphQL query to retrieve the data used to populate the lunar preview calendar (next 7 days)
 *
 * @param {Geographic} coord - a Geographic object containing lat/lon coords for the requested location
 * @returns {Object} - the weather data for the lunar calendar for the requested location
 */
export const getLunarForecastQuery = async (coord) => {
  const query = gql`
    query FetchSolarLunarData(
      $location: WeatherLocationInput!
      $to: Time!
      $from: Time!
    ) {
      solarLunarData(from: $from, to: $to, location: $location) {
        timezone
        periods {
          startTime
          endTime
          lunar {
            overhead
            underfoot
            set
            rise
            phaseName
            illumination
          }
        }
      }
    }
  `

  const location = {
    coordinate: formatLocationForQuery(coord),
  }

  // from midnight of current day
  let from = new Date()
  from.setHours(0, 0, 0, 0)
  from = new Date(from).toISOString()

  // to 7 days later
  let to = new Date()
  to.setMinutes(0, 0, 0)
  to.setDate(to.getDate() + 7)
  to = new Date(to).toISOString()

  const client = await getApolloClient()

  const response = await client.query({
    query,
    variables: {
      location,
      to,
      from,
    },
  })

  return response
}

/**
 * Formats a location for use in a weather query
 * @param {Geographic} location - Geographic location for the weather query
 * @returns {object} formatted location for the weather query
 */
export const formatLocationForQuery = (location) => {
  return {
    // round coordinates to 3 decimal places for caching purposes
    lat: Number(location?.latitude?.toFixed(3)),
    lon: Number(location?.longitude?.toFixed(3)),
  }
}
